/**
 * Reading Time Calculation
 *
 * Calculates estimated reading time for Living Project Guide pages.
 * Supports both template-only pages and Living Articles with additional content.
 */

import type { ProjectTemplate } from '../types'

const WORDS_PER_MINUTE = 200

/**
 * Count words in a string
 */
function countWords(text: string | undefined): number {
  if (!text) return 0
  return text.trim().split(/\s+/).filter(Boolean).length
}

/**
 * Calculate reading time in minutes for a project template
 *
 * Content sources:
 * - Overview/description
 * - Step descriptions and tips
 * - FAQ answers
 */
export function calculateReadingTime(template: ProjectTemplate): number {
  let totalWords = 0

  // Overview content
  totalWords += countWords(template.overviewContent ?? template.description)

  // Step descriptions and tips
  for (const step of template.steps) {
    totalWords += countWords(step.description)
    if (step.tips) {
      totalWords += countWords(step.tips.join(' '))
    }
    if (step.warnings) {
      totalWords += countWords(step.warnings.join(' '))
    }
  }

  // FAQ answers
  if (template.faqItems) {
    for (const faq of template.faqItems) {
      totalWords += countWords(faq.answer)
    }
  }

  return Math.max(1, Math.ceil(totalWords / WORDS_PER_MINUTE))
}
